import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { SWIPE_BACK_EDGE_WIDTH } from '@/hooks/useSwipeBack';
import type { BracketMatch, BracketRound } from '@/utils/knockoutBracket';
import { RoundPills } from '../RoundPills';
import { TieCard, type TieCardSide } from '../TieCard';
import { columnLayout, openRoundIndex, roundLabel, sideLabel, tieCaption } from '../bracketView';
import 'swiper/css';
import styles from './styles.module.scss';

/** Narrowest round column, in px of the strip's own width. */
const MIN_COLUMN_WIDTH = 160;

export interface KnockoutBracketProps {
  /** Rounds of `buildKnockoutBracket`, first round first; not empty. */
  rounds: readonly BracketRound<BracketMatch>[];
}

/**
 * One-sided play-off bracket: a row of round pills over a strip of round
 * columns that slides sideways round by round - two columns on a phone, more
 * as the width allows, no pills once every round fits. The strip is as tall
 * as the tallest round on screen, and a change of round shows its first tie.
 *
 * The strip is a slider nested in the page-tab slider: it takes a sideways
 * swipe and passes it to the page tabs only past its first or last round.
 */
export const KnockoutBracket: React.FC<KnockoutBracketProps> = ({ rounds }) => {
  const { t } = useTranslation('league');
  const [strip, setStrip] = useState<SwiperType | null>(null);
  const [allRoundsFit, setAllRoundsFit] = useState(false);
  const [openRound] = useState(() => openRoundIndex(rounds));
  const [activeRound, setActiveRound] = useState(openRound);
  // The first round on screen: the strip cannot move as far as the last rounds.
  const [firstVisible, setFirstVisible] = useState(openRound);
  const rootRef = useRef<HTMLDivElement>(null);
  // A round picked by its pill stays the filled one until the strip is dragged:
  // the last rounds share one rest position, which alone cannot tell them apart.
  const isPicked = useRef(true);

  const lastRound = rounds.length - 1;
  const layout = columnLayout(rounds, firstVisible);

  // The slider measures the rounds on screen only when it moves; any render can change their height.
  useLayoutEffect(() => {
    if (strip && !strip.destroyed) strip.updateAutoHeight();
  });

  // One more column per `MIN_COLUMN_WIDTH` of strip width, up to the number of rounds.
  const breakpoints = useMemo(() => {
    const byWidth: Record<number, SwiperOptions> = {};
    for (let columns = 3; columns <= rounds.length; columns++) {
      byWidth[columns * MIN_COLUMN_WIDTH] = { slidesPerView: columns };
    }
    return byWidth;
  }, [rounds.length]);

  // Scrolls the page back to the top of the bracket, so the first tie of the round is in view.
  // The pill row is short, always inside the page width and outside the strip: nothing else scrolls.
  const showFirstTie = () => rootRef.current?.firstElementChild?.scrollIntoView({ block: 'nearest' });

  const handleSlideChange = (swiper: SwiperType) => {
    setFirstVisible(swiper.activeIndex);
    if (!isPicked.current) setActiveRound(swiper.isEnd ? lastRound : swiper.activeIndex);
  };

  const handleSelect = (index: number) => {
    isPicked.current = true;
    setActiveRound(index);
    strip?.slideTo(index);
    showFirstTie();
  };

  return (
    <div ref={rootRef} className={styles.bracket} data-testid="knockout-bracket">
      {!allRoundsFit && (
        <RoundPills
          labels={rounds.map(roundLabel)}
          activeIndex={activeRound}
          onSelect={handleSelect}
          ariaLabel={t('league:bracketRounds')}
        />
      )}

      <Swiper
        className={styles.strip}
        nested
        // No rubber band past the first and last round: that drag belongs to the page tabs.
        resistanceRatio={0}
        slidesPerView={2}
        breakpoints={breakpoints}
        breakpointsBase="container"
        autoHeight
        initialSlide={openRound}
        // Edge swipes belong to the app-wide swipe-back (useSwipeBack).
        edgeSwipeDetection
        edgeSwipeThreshold={SWIPE_BACK_EDGE_WIDTH}
        onSwiper={swiper => {
          setStrip(swiper);
          setFirstVisible(swiper.activeIndex);
        }}
        onSlideChange={handleSlideChange}
        // A slide the user made; the slider's own moves (first position, resize) leave the page alone.
        onSlideChangeTransitionStart={() => {
          if (!isPicked.current) showFirstTie();
        }}
        onSliderFirstMove={() => {
          isPicked.current = false;
        }}
        onLock={() => setAllRoundsFit(true)}
        onUnlock={() => setAllRoundsFit(false)}
      >
        {rounds.map((round, r) => {
          const { startsGroup, span, linksNext, merges } = layout[r];
          const isFinal = r === lastRound && round.ties.length === 1;
          // Only a round not stored yet waits for its draw; a stored round shows its pairs.
          const isDrawn = startsGroup && r > 0 && !round.isStored;
          const columnClass = [
            styles.column,
            round.isStored ? null : styles.placeholders,
            isDrawn ? styles.drawn : null,
            startsGroup ? null : styles.linkedPrevious,
            linksNext ? styles.linksNext : null,
            merges ? styles.merges : null,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <SwiperSlide key={r} className={columnClass} data-testid="bracket-round">
              <div className={styles.head}>
                <span>{roundLabel(round)}</span>
                {isDrawn && <span className={styles.drawMark}>{t('league:bracketPairsSetByDraw')}</span>}
              </div>
              <div className={styles.slots} style={{ '--tie-span': span } as React.CSSProperties}>
                {round.ties.map(tie => (
                  <div key={tie.id} className={styles.slot}>
                    <TieCard
                      caption={tieCaption(tie, round)}
                      sides={[0, 1].map(index => {
                        const side = tie.sides[index];
                        return {
                          label: sideLabel(side, rounds),
                          logoUrl: side.logoUrl,
                          score: side.aggregate,
                          isEliminated: tie.winner !== null && tie.winner !== index,
                        };
                      }) as [TieCardSide, TieCardSide]}
                      isPlaceholder={!round.isStored}
                      isFinal={isFinal}
                    />
                  </div>
                ))}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default KnockoutBracket;
